import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  TextField,
  Stack,
  LinearProgress,
  Alert
} from '@mui/material';
import api from '../api/axios';
import { isAssessmentSubtask } from '../utils/contentGenerationSubtasks';
import AssessmentPayloadEditor from './AssessmentPayloadEditor';

const STEPS = ['Context', 'Type', 'IAC criteria', 'Parameters', 'Review'];

function getApiBase() {
  return (api.defaults.baseURL || '').replace(/\/$/, '');
}

async function postAssessmentGenerateStream(qualificationId, body, onEvent) {
  const token = localStorage.getItem('token');
  const url = `${getApiBase()}/qualifications/${qualificationId}/learning-content/task-assessments/generate-stream`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && token !== 'undefined' ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const reader = res.body && res.body.getReader();
  if (!reader) {
    throw new Error('No response body');
  }
  const dec = new TextDecoder();
  let buffer = '';
  let last = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += dec.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const block of parts) {
      const line = block.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        const data = JSON.parse(line.slice(6));
        last = data;
        if (onEvent) onEvent(data);
      } catch (_) {}
    }
  }
  return last;
}

export default function TaskAssessmentWizardModal({
  open,
  onClose,
  qualificationId,
  moduleId,
  assignedItems = {},
  /** When set, this task stays selected and is the canonical task_code for generation. */
  task,
  onSaved
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [kind, setKind] = useState(null);
  const [selectedCodes, setSelectedCodes] = useState(() => new Set());
  const [questionCount, setQuestionCount] = useState(20);
  const [totalMarks, setTotalMarks] = useState(20);
  const [optionCount, setOptionCount] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contextCodes, setContextCodes] = useState(() => new Set());
  const [assessmentFileRows, setAssessmentFileRows] = useState([]);
  /** When true, backend rejects generation unless every selected IAC appears in some question's iac_codes. */
  const [enforceIacCoverage, setEnforceIacCoverage] = useState(true);
  /** Allow same IAC on multiple questions (default off). */
  const [allowRepeatIac, setAllowRepeatIac] = useState(false);
  /** IAC codes missing from model output when enforcement is off (shown as warning on review). */
  const [iacCoverageWarningCodes, setIacCoverageWarningCodes] = useState(null);

  const wizardSessionRef = useRef(null);
  const defaultIacsAppliedRef = useRef(false);

  const moduleTasks = useMemo(() => {
    if (moduleId == null || !assignedItems) return [];
    const m = assignedItems[moduleId] || { KT: [], PS: [] };
    return [...(m.KT || []), ...(m.PS || [])];
  }, [moduleId, assignedItems]);

  /** First task in module order (KT then PS) among selected BoK tasks; fixed `task` prop wins. */
  const canonicalTask = useMemo(() => {
    if (task?.code) {
      return moduleTasks.find((t) => t.code === task.code) || task;
    }
    for (const t of moduleTasks) {
      if (contextCodes.has(t.code)) return t;
    }
    return null;
  }, [task, moduleTasks, contextCodes]);

  const selectedBoKKey = useMemo(
    () => Array.from(contextCodes).sort().join(','),
    [contextCodes]
  );

  const sumCreditsSelected = useMemo(() => {
    let sum = 0;
    for (const t of moduleTasks) {
      if (!contextCodes.has(t.code)) continue;
      const c = t.credits != null ? Number(t.credits) : 0;
      if (!Number.isNaN(c)) sum += c;
    }
    return sum;
  }, [moduleTasks, contextCodes]);

  const loadAssessmentFiles = useCallback(async () => {
    if (!qualificationId || !moduleTasks.length) {
      setAssessmentFileRows([]);
      return;
    }
    const codes = moduleTasks.map((t) => t.code).filter(Boolean);
    if (!codes.length) {
      setAssessmentFileRows([]);
      return;
    }
    try {
      const res = await api.get(
        `/qualifications/${qualificationId}/learning-content/bok-documents/assessment-files`,
        { params: { task_codes: codes.join(',') } }
      );
      setAssessmentFileRows(res.data?.files || []);
    } catch (_) {
      setAssessmentFileRows([]);
    }
  }, [qualificationId, moduleTasks]);

  const fileRowByCode = useMemo(() => {
    const m = {};
    for (const row of assessmentFileRows) {
      if (row?.subtask_code) m[row.subtask_code] = row;
    }
    return m;
  }, [assessmentFileRows]);

  useEffect(() => {
    if (!open) {
      wizardSessionRef.current = null;
      defaultIacsAppliedRef.current = false;
      return;
    }
    const key = `${moduleId}-${task?.code || 'pick'}`;
    if (wizardSessionRef.current === key) return;
    wizardSessionRef.current = key;
    defaultIacsAppliedRef.current = false;
    setActiveStep(0);
    setKind(null);
    setSelectedCodes(new Set());
    setQuestionCount(20);
    setTotalMarks(20);
    setOptionCount(4);
    setGenerating(false);
    setProgressMsg('');
    setError(null);
    setAssessmentId(null);
    setPayload(null);
    setSaving(false);
    setEnforceIacCoverage(true);
    setAllowRepeatIac(false);
    setIacCoverageWarningCodes(null);
    if (task?.code) {
      setContextCodes(new Set([task.code]));
    } else {
      setContextCodes(new Set());
    }
  }, [open, moduleId, task?.code]);

  useEffect(() => {
    if (!open) return;
    loadAssessmentFiles();
  }, [open, loadAssessmentFiles]);

  useEffect(() => {
    if (!open || task) return;
    if (moduleTasks.length !== 1) return;
    setContextCodes((prev) => {
      if (prev.size > 0) return prev;
      const c = moduleTasks[0].code;
      return c ? new Set([c]) : prev;
    });
  }, [open, task, moduleTasks]);

  useEffect(() => {
    if (!open || !task?.code) return;
    setContextCodes((prev) => {
      const next = new Set(prev);
      next.add(task.code);
      return next;
    });
  }, [open, task?.code]);

  const iacNodes = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of moduleTasks) {
      if (!contextCodes.has(t.code)) continue;
      const children = t.children || [];
      for (const c of children) {
        if (!isAssessmentSubtask(c)) continue;
        const code = c.code;
        if (!code || seen.has(code)) continue;
        seen.add(code);
        out.push(c);
      }
    }
    return out;
  }, [moduleTasks, contextCodes]);

  useEffect(() => {
    if (!open || defaultIacsAppliedRef.current) return;
    if (!iacNodes.length) return;
    setSelectedCodes(new Set(iacNodes.map((n) => n.code)));
    defaultIacsAppliedRef.current = true;
  }, [open, selectedBoKKey, iacNodes]);

  useEffect(() => {
    defaultIacsAppliedRef.current = false;
  }, [selectedBoKKey]);

  useEffect(() => {
    if (!open || contextCodes.size === 0) return;
    const c = sumCreditsSelected;
    if (kind === 'free_text' || kind === 'case_study') {
      const def = Math.round(c * 10);
      setTotalMarks(def > 0 ? def : 35);
      setQuestionCount((q) => (q === 20 ? 5 : q));
    } else if (kind === 'mcq' || kind === 'true_false') {
      setQuestionCount(20);
      setTotalMarks(20);
      if (kind === 'mcq') setOptionCount(4);
    }
  }, [kind, open, sumCreditsSelected, contextCodes.size]);

  const toggleIac = (code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const selectAllIacs = () => {
    setSelectedCodes(new Set(iacNodes.map((n) => n.code)));
  };

  const deselectAllIacs = () => {
    setSelectedCodes(new Set());
  };

  const selectAssessmentKind = (k) => {
    setError(null);
    setKind(k);
    setActiveStep(iacNodes.length > 0 ? 2 : 3);
  };

  const selectedIacPayload = useMemo(() => {
    return iacNodes
      .filter((n) => selectedCodes.has(n.code))
      .map((n) => ({ code: n.code, title: n.title || n.code }));
  }, [iacNodes, selectedCodes]);

  const toggleContextCode = (code) => {
    if (task?.code === code) return;
    setContextCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        if (next.size <= 1) return prev;
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const validateContextStep = () => {
    if (contextCodes.size === 0) {
      setError('Select at least one task with synced BoK content for this assessment.');
      return false;
    }
    if (!canonicalTask) {
      setError('Could not determine a task for this assessment.');
      return false;
    }
    const selected = Array.from(contextCodes);
    const missing = selected.filter((c) => !fileRowByCode[c]?.has_openai_file);
    if (missing.length) {
      setError(`Open View PDF (or sync) for: ${missing.join(', ')} before including them.`);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError(null);
    if (activeStep === 0) {
      if (!validateContextStep()) return;
      setActiveStep(1);
      return;
    }
    if (activeStep === 1 && !kind) {
      setError('Choose an assessment type');
      return;
    }
    if (activeStep === 2 && iacNodes.length > 0 && selectedCodes.size === 0) {
      setError('Select at least one assessment criterion');
      return;
    }
    if (activeStep === 3) {
      runGenerate();
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const runGenerate = async () => {
    if (!canonicalTask) return;
    setError(null);
    setGenerating(true);
    setProgressMsg('Starting…');
    const context_task_codes = Array.from(contextCodes).filter(Boolean);
    try {
      const last = await postAssessmentGenerateStream(
        qualificationId,
        {
          kind,
          task_code: canonicalTask.code,
          module_id: moduleId,
          task_title: canonicalTask.title,
          context_task_codes,
          iacs: selectedIacPayload,
          question_count: questionCount,
          total_marks: totalMarks,
          option_count: optionCount,
          credits_snapshot: sumCreditsSelected,
          allow_repeat_iac: allowRepeatIac,
          ...(enforceIacCoverage ? { enforce_iac_coverage: true } : { enforce_iac_coverage: false })
        },
        (ev) => {
          if (ev.type === 'status' && ev.message) setProgressMsg(ev.message);
          if (ev.type === 'error') setError(ev.message || 'Generation failed');
        }
      );
      if (last && last.type === 'result' && last.assessment) {
        setAssessmentId(last.assessment.id);
        setPayload(last.assessment.payload);
        const missing = last.warnings && Array.isArray(last.warnings.missing_iac_codes)
          ? last.warnings.missing_iac_codes
          : null;
        setIacCoverageWarningCodes(missing && missing.length ? missing : null);
        setActiveStep(4);
      } else if (last && last.type === 'error') {
        setError(last.message || 'Generation failed');
      } else {
        setError('Unexpected response from server');
      }
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
      setProgressMsg('');
    }
  };

  const savePayload = async (status) => {
    if (!assessmentId || !payload) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(
        `/qualifications/${qualificationId}/learning-content/task-assessments/${assessmentId}`,
        { payload, status: status || 'saved' }
      );
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const reviewStepIndex = 4;
  const lastContentStepBeforeReview = 3;

  const handleBack = () => {
    if (activeStep === 3 && iacNodes.length === 0 && kind) {
      setActiveStep(1);
      return;
    }
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const dialogAssessmentLabel =
    canonicalTask &&
    (contextCodes.size > 1
      ? `${canonicalTask.title || canonicalTask.code} (+${contextCodes.size - 1} more)`
      : canonicalTask.title || canonicalTask.code);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>New task assessment — {dialogAssessmentLabel || 'Select tasks'}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ py: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select which tasks to include (BoK text for file_search). IAC criteria are combined from all selected
              tasks. The stored task code for this assessment follows module order (KT then PS) among your selection.
              Each selected task must be synced to OpenAI (use View PDF once per task).
            </Typography>
            {task && (
              <Typography variant="body2">
                <strong>Anchor task:</strong> {task.code} — {task.title || task.code} (always included; defines stored
                task_code)
              </Typography>
            )}
            <Typography variant="subtitle2">BoK tasks for this assessment</Typography>
            <Box sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {moduleTasks.map((t) => {
                const row = fileRowByCode[t.code];
                const hasFile = Boolean(row?.has_openai_file);
                const isFixed = task?.code === t.code;
                const checked = contextCodes.has(t.code);
                return (
                  <FormControlLabel
                    key={t.code}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={!hasFile || isFixed}
                        onChange={() => toggleContextCode(t.code)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" component="span">
                          {t.code} — {t.title || t.code}
                        </Typography>
                        {!hasFile && (
                          <Typography variant="caption" color="warning.main" display="block">
                            Not synced — open View PDF for this task first
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Choose a format to continue to criteria and parameters.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="large" onClick={() => selectAssessmentKind('mcq')}>
                Multiple choice (MCQ)
              </Button>
              <Button variant="contained" color="secondary" size="large" onClick={() => selectAssessmentKind('free_text')}>
                Free text
              </Button>
              <Button variant="outlined" size="large" onClick={() => selectAssessmentKind('true_false')}>
                True / False
              </Button>
              <Button variant="outlined" size="large" onClick={() => selectAssessmentKind('case_study')}>
                Case study
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 2 && (
          <Box>
            {iacNodes.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" onClick={selectAllIacs}>
                  Select all
                </Button>
                <Button size="small" variant="outlined" onClick={deselectAllIacs}>
                  Deselect all
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {selectedCodes.size} of {iacNodes.length} selected (defaults to all)
                </Typography>
              </Stack>
            )}
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              {iacNodes.length === 0 ? (
                <Typography color="text.secondary">No IAC / assessment criteria on the selected tasks.</Typography>
              ) : (
                iacNodes.map((n) => (
                  <FormControlLabel
                    key={n.code}
                    control={
                      <Checkbox checked={selectedCodes.has(n.code)} onChange={() => toggleIac(n.code)} />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {n.code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {n.title}
                        </Typography>
                      </Box>
                    }
                  />
                ))
              )}
            </Box>
          </Box>
        )}

        {activeStep === 3 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Number of questions"
              type="number"
              value={questionCount}
              onChange={(e) => setQuestionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, max: 100 }}
            />
            <TextField
              label="Total marks"
              type="number"
              value={totalMarks}
              onChange={(e) => setTotalMarks(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, max: 1000 }}
            />
            {kind === 'mcq' && (
              <TextField
                label="Options per question"
                type="number"
                value={optionCount}
                onChange={(e) =>
                  setOptionCount(Math.min(8, Math.max(2, parseInt(e.target.value, 10) || 4)))
                }
                inputProps={{ min: 2, max: 8 }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowRepeatIac}
                  onChange={(e) => setAllowRepeatIac(e.target.checked)}
                />
              }
              label="Allow repeated IAC assessment (same IAC on multiple questions; requires different cognitive levels)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={enforceIacCoverage}
                  onChange={(e) => setEnforceIacCoverage(e.target.checked)}
                />
              }
              label="Require every selected IAC in this run (stricter; generation fails if the model omits any)"
            />
            {generating && (
              <Box>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1 }} display="block">
                  {progressMsg || 'Working…'}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {activeStep === reviewStepIndex && payload && (
          <>
            {iacCoverageWarningCodes && iacCoverageWarningCodes.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The generated questions do not tag every selected IAC yet. Missing from question{' '}
                <code>iac_codes</code>: {iacCoverageWarningCodes.join(', ')}. You can edit tags below, generate
                another assessment later, or use the strict option on the previous step to force a retry.
              </Alert>
            )}
            {payload?.metadata?.generation_warnings?.personal_data_flags?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some questions may request personal information. Edit scenarios to use fictional case studies.
              </Alert>
            )}
            {payload?.metadata?.generation_warnings?.duplicate_question_pairs?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Possible duplicate questions were detected. Review or regenerate individual questions below.
              </Alert>
            )}
            <AssessmentPayloadEditor
              kind={kind}
              payload={payload}
              onChange={setPayload}
              qualificationId={qualificationId}
              assessmentId={assessmentId}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && activeStep < reviewStepIndex && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep === 0 && (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        )}
        {activeStep === 2 && (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        )}
        {activeStep === lastContentStepBeforeReview && (
          <Button variant="contained" onClick={handleNext} disabled={generating}>
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        )}
        {activeStep === reviewStepIndex && (
          <>
            <Button variant="outlined" disabled={saving} onClick={() => savePayload('saved')}>
              Save
            </Button>
            <Button variant="contained" disabled={saving} onClick={() => savePayload('approved')}>
              Save and approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
